import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { RunDetail } from "@/components/agents/RunDetail";
import { RunLauncher } from "@/components/agents/RunLauncher";
import { RunsList } from "@/components/agents/RunsList";
import { api, type AgentId } from "@/lib/api";
import { isAuthenticated, pb } from "@/lib/pb";

export const Route = createFileRoute("/agents/$id")({
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: AgentDetail,
});

function AgentDetail() {
  const { id } = Route.useParams();
  const agentId = id as AgentId;
  const [selected, setSelected] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const qc = useQueryClient();

  const registryQuery = useQuery({
    queryKey: ["agents-registry"],
    queryFn: () => api.agents.registry(),
    staleTime: 5 * 60_000,
  });
  const agent = registryQuery.data?.agents.find((a) => a.id === agentId) ?? null;

  const runsQuery = useQuery({
    queryKey: ["agent-runs", agentId],
    queryFn: () => api.agents.runs(30, agentId),
    refetchInterval: (query) =>
      (query.state.data?.runs ?? []).some((r) => r.status === "queued" || r.status === "running")
        ? 15_000
        : 60_000,
  });

  const weeksQuery = useQuery({
    queryKey: ["reports-index"],
    queryFn: () => api.reports.list(),
    enabled: (agent?.modes.length ?? 0) > 0,
  });

  useEffect(() => {
    const unsub = pb
      .collection("agent_runs")
      .subscribe("*", () => qc.invalidateQueries({ queryKey: ["agent-runs", agentId] }))
      .catch(() => () => {});
    return () => {
      Promise.resolve(unsub).then((fn) => typeof fn === "function" && fn());
    };
  }, [qc, agentId]);

  const runs = runsQuery.data?.runs ?? [];

  useEffect(() => {
    if (!selected && runs.length > 0) setSelected(runs[0].id);
  }, [runs, selected]);

  const launch = useMutation({
    mutationFn: ({ mode, week, priorities }: { mode: string; week: string; priorities: string }) =>
      api.agents.run({ mode: mode as never, week, priorities }),
    onSuccess: async (res) => {
      setNotice(
        res.dry_run
          ? "DRY_RUN activo en el backend — no se disparó nada."
          : "Corrida encolada. Aparece acá apenas GitHub la registre.",
      );
      setTimeout(() => {
        api.agents
          .sync()
          .then(() => qc.invalidateQueries({ queryKey: ["agent-runs", agentId] }))
          .catch((e) => console.error("[agents] sync failed", e));
      }, 8000);
    },
    onError: (e: Error) => setNotice(`Error: ${e.message}`),
  });

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 8000);
    return () => clearTimeout(t);
  }, [notice]);

  if (registryQuery.isLoading) return <div className="text-muted">cargando…</div>;
  if (!agent) {
    return (
      <div className="max-w-3xl mx-auto space-y-3">
        <p className="path-chip">/agents/{id}</p>
        <p className="text-danger font-mono text-sm">agente desconocido: {id}</p>
        <Link to="/agents" className="text-primary font-mono text-sm">
          ← volver a la grilla
        </Link>
      </div>
    );
  }

  const selectedRun = runs.find((r) => r.id === selected) ?? null;
  const activeCount = runs.filter((r) => r.status === "running" || r.status === "queued").length;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <Link to="/agents" className="path-chip hover:text-primary transition-colors">
            /agents
          </Link>
          <h1 className="text-3xl font-semibold mt-1">{agent.name}</h1>
          <p className="text-sm text-muted mt-1 max-w-2xl">{agent.does}</p>
        </div>
        <p className="font-mono text-xs text-muted shrink-0">
          {activeCount} activas · {runs.length} totales
        </p>
      </div>

      {notice && (
        <div className="surface px-4 py-3 font-mono text-sm text-primary border-primary/30">
          {notice}
        </div>
      )}

      {runsQuery.error && (
        <div className="surface px-4 py-3 font-mono text-sm text-danger">
          {(runsQuery.error as Error).message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[22rem_1fr] gap-6 items-start">
        <div className="space-y-4 lg:sticky lg:top-6">
          <RunLauncher
            modes={agent.modes}
            workflow={agent.workflow}
            weeks={weeksQuery.data?.weeks ?? []}
            busy={launch.isPending}
            onLaunch={(mode, week, priorities) => launch.mutate({ mode, week, priorities })}
          />
          <RunsList runs={runs} selectedId={selected} onSelect={setSelected} />
        </div>

        <RunDetail run={selectedRun} />
      </div>
    </div>
  );
}
